/** One bucket in a record-service facet response. */
export interface CollectionFacetBucket {
  value: string;
  count: number;
}

/** Discriminates how a facet's buckets should be interpreted by consumers. */
export type CollectionFacetType = "enum" | "boolean" | "terms";

/** Parsed facet for one schema field. */
export interface CollectionFacet {
  fieldId: string;
  type: CollectionFacetType;
  buckets: CollectionFacetBucket[];
}

/** Facet `type` discriminants the FE knows how to render. */
const KNOWN_FACET_TYPES: ReadonlySet<string> = new Set<CollectionFacetType>([
  "enum",
  "boolean",
  "terms",
]);

const isCollectionFacetBucket = (value: unknown): value is CollectionFacetBucket => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const bucket = value as { value?: unknown; count?: unknown };
  return typeof bucket.value === "string" && typeof bucket.count === "number";
};

/**
 * Coerces `CollectionQueryResponse.facets` into typed facets.
 *
 * Enum, boolean, and terms facets all share the `{ value, count }` bucket
 * shape, so they parse through one path and carry their `type` discriminant for
 * consumers to branch on. Unknown fields and unrecognised facet types are
 * skipped.
 */
export const parseFacets = (raw: unknown): CollectionFacet[] => {
  if (!raw || typeof raw !== "object") {
    return [];
  }

  return Object.entries(raw as Record<string, unknown>)
    .map(([fieldId, facet]): CollectionFacet | null => {
      if (!facet || typeof facet !== "object") {
        return null;
      }

      const typed = facet as { type?: unknown; buckets?: unknown };
      if (
        typeof typed.type !== "string" ||
        !KNOWN_FACET_TYPES.has(typed.type) ||
        !Array.isArray(typed.buckets)
      ) {
        return null;
      }

      const buckets = typed.buckets.filter(isCollectionFacetBucket);
      if (buckets.length === 0) {
        return null;
      }

      return { fieldId, type: typed.type as CollectionFacetType, buckets };
    })
    .filter((facet): facet is CollectionFacet => facet !== null);
};
